/* eslint-disable no-use-before-define */
import { GoogleSignin } from '@react-native-google-signin/google-signin';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback } from 'react';
import { KeyboardAvoidingView } from 'react-native';
import { MaskedTextInput } from 'react-native-advanced-input-mask';
import { getHash } from 'react-native-otp-verify';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
    BigButton,
    Field,
    Image,
    InputWrapper,
    Keyboard,
    Link,
    Logo,
    Page,
    Platform,
    React,
    Section,
    TextInput
} from '../../common';
import {
    COUNTRY_CODE,
    MODE,
    SIGNIN_MASK,
    SIGNIN_MASK_MOBILE,
    USE_SMS_ASSISTANT,
    VERSION
} from '../../config';
import {
    getDefaultSmsProvider,
    getIsUserFetching,
    getSecondSmsProvider,
    getUserError,
    getUserLoadingState
} from '../../selectors';
import api from '../../services/api';
import { eventBus } from '../../services/eventBus';
import { FIRST_AID_KIT_SYNC_KEY } from '../../services/firstAidKit';
import { storage } from '../../services/localstorage';
import { user } from '../../services/user';
import { putIntoStore } from '../../store';
import { input as st } from '../../styles/input';
import { getMaskByPhonePrefix } from '../../utils';
import {
    isPhoneNumberValid,
    isTestPhoneNumber,
    maskNumbers
} from '../../utils/phone';
import { insertIntoString } from '../../utils/strings';
import { checkRestore } from '../Services/Backup/common/backupRestore';

// const PhoneEdit = ({
//     onChangeText,
//     placeholder,
//     value,
//     style,
//     ns,
//     disabled,
//     textStyle,
//     multiline,
//     hideText,
//     selection,
//     placeholderTextColor,
//     invalid,
//     ...props
// }) => {
//     const [currentSelection, setCurrentSelection] = useState(selection);
//     if (disabled) {
//         return <View style={[st.input, st.disabled, style]}><Text style={textStyle}>{value}</Text></View>;
//     }
//     return (
//         <InputWrapper>
//             <TextInputRN
//                 onChangeText={onChangeText}
//                 value={value}
//                 style={[
//                     st.input,
//                     style,
//                     textStyle,
//                     invalid && value ? st.invalid : null,
//                     Platform.OS === 'web' && {
//                         outline: 'none',
//                         cursor: 'text',
//                     },
//                 ]}
//                 accessibilityLabel={`input:${ns}`}
//                 underlineColorAndroid="transparent"
//                 placeholder={Object.R(placeholder)}
//                 secureTextEntry={hideText}
//                 placeholderTextColor={placeholderTextColor}
//                 numberOfLines={1}
//                 multiline={multiline}
//                 onKeyPress={({ nativeEvent }) => {
//                 }
//                 }
//                 onSelectionChange={({ nativeEvent }) => {
//                     setCurrentSelection(nativeEvent);
//                 }
//                 }
//                 {...props}
//             />
//         </InputWrapper>
//     );
// };

/**
 * The SignIn by phone number page.
 */
export const SignIn = Page.register(
    // ui composition
    ({
        loginId,
        isLoading,
        error,
        isFetching,
        deeplinkPhoneNumber,
        phonePrefix = COUNTRY_CODE,
        phoneNumber = deeplinkPhoneNumber
            ? maskNumbers(
                getMaskByPhonePrefix(phonePrefix.replace('+',
                    '')),
                `${deeplinkPhoneNumber}`.substring(3)
                    .replace(/\D+/g,
                        ''),
            ) || ''
            : '',

        isValid = isPhoneNumberValid(phonePrefix + phoneNumber),
        setState,
        actions,
        navigation,
        captchaCodeImg,
        captchaCodeNum,
        waitingResponse,
        defaultSmsProvider,
        isCaptchaSubmitted = false,
        emailLoginEnabled,
        onPhoneNumber = (s) => {
            let finalNumber = s;
            if (s.length > phoneNumber.length) {
                finalNumber = maskNumbers(phoneMask,
                    s.replace(/\D+/g,
                        ''));
            }
            setState({
                phoneNumber: finalNumber,
                isValid: isPhoneNumberValid(phonePrefix + s),
            });
        },
        onCaptchaCode = (code) => {
            setState({ captchaCodeNum: code });
        },
        // onDidMount = () => storage.getObject('phoneNumber', '')
        //     .then((phone) => {
        //         // If phone number is longer than 10 - this phone number is from old versions and needs clear
        //         if (!phonePrefix.replace(/\D+/g, '').length && phone.replace(/\D+/g, '').length > 10) {
        //             // eslint-disable-next-line no-param-reassign
        //             phone = '';
        //         }
        //         setState({
        //             phoneNumber: phone,
        //             isValid: isPhoneNumberValid(phonePrefix + phone),
        //         });
        //     }),
        onSubmitCaptchaCode = async () => {
            setState({ isCaptchaSubmitted: true });
            try {
                const resp = await user.captchaCodeConfirmation(Number(captchaCodeNum),
                    loginId,
                    actions.setUserError);
                Keyboard.dismiss();
                if (resp?.id) {
                    setState({ captchaCodeImg: null });
                    navigation.navigate('CodeConfirmation',
                        {
                            phoneNumber: `${
                                phonePrefix.replace('+',
                                    '').length === 4
                                    ? insertIntoString(phonePrefix,
                                        '-',
                                        2)
                                    : phonePrefix
                            } ${phoneNumber}`,
                            id: resp.id,
                            isSignInWithApi: true,
                        });
                }
                setState({ waitingResponse: false });
            } finally {
                setState({ isCaptchaSubmitted: false });
            }
        },
        // eslint-disable-next-line max-statements
        onSubmitPhone = async () => {
            // await storage.set('bumbaBanner', true);
            // actions.setFormData({ bumbaBanner: true });
            const fullPhoneNumber = `+${(phonePrefix + phoneNumber).replace(/\D+/g,
                '')}`;
            // console.log('fullPhoneNumber', { fullPhoneNumber, isTest: isTestPhoneNumber(fullPhoneNumber) });
            // return;
            const phoneNumberForCodeConfirmation = `${
                phonePrefix.replace('+',
                    '').length === 4 ? insertIntoString(phonePrefix,
                        '-',
                        2) : phonePrefix
            } ${phoneNumber}`;
            if (isTestPhoneNumber(fullPhoneNumber)) {
                let appHash = [];
                if (Platform.OS === 'android') {
                    appHash = await getHash();
                }
                actions.startUserFetch();
                const {
                    id,
                    captchaCode,
                } =
                (await user.signIn(fullPhoneNumber,
                    appHash[0],
                    (message, errorCode) => {
                        actions.stopUserFetch();
                        actions.setUserError(message,
                            errorCode);
                    })) || {};
                actions.stopUserFetch();
                if (id) {
                    storage.set('phoneNumber',
                        phoneNumber);
                    if (captchaCode) {
                        setState({
                            captchaCodeImg: captchaCode,
                            loginId: id,
                        });
                    } else {
                        try {
                            GoogleSignin?.signOut?.();
                        } catch {
                            //
                        }
                        navigation.navigate('CodeConfirmation',
                            {
                                phoneNumber: phoneNumberForCodeConfirmation,
                                id,
                                isSignInWithApi: true,
                            });
                    }
                }
            } else if (defaultSmsProvider === 'fb' || !USE_SMS_ASSISTANT) {
                try {
                    actions.signInWithPhoneNumber(fullPhoneNumber,
                        (confirmation) => {
                            storage.set('phoneNumber',
                                phoneNumber);
                            navigation.navigate('CodeConfirmation',
                                {
                                    phoneNumber: phoneNumberForCodeConfirmation,
                                    confirmation,
                                    isSignInWithApi: false,
                                });
                        });
                } catch {
                    // console.log('Error login', e);
                }
            } else {
                let appHash = [];
                if (Platform.OS === 'android') {
                    appHash = await getHash();
                }
                const {
                    id: newId,
                    captchaCode,
                } = await user.signIn(
                    `+${fullPhoneNumber.replace(/\D+/g,
                        '')}`,
                    appHash[0],
                    (e) => {
                        actions.setUserError(e);
                        setState({ waitingResponse: false });
                    },
                );

                if (captchaCode) {
                    actions.setUserError();
                    setState({
                        captchaCodeImg: captchaCode,
                        loginId: newId,
                    });
                } else {
                    storage.set('phoneNumber',
                        phoneNumber);
                    navigation.navigate('CodeConfirmation',
                        {
                            phoneNumber: phoneNumberForCodeConfirmation,
                            confirmation: newId,
                            isSignInWithApi: true,
                            id: newId,
                        });
                }
            }
            setState({ waitingResponse: false });
        },
        onPhonePrefix = (text) => {
            let prefix = text.replace(/\D+/g,
                '');
            if (text.length > 0) {
                prefix = `+${prefix}`;
            }
            setState({
                phonePrefix: prefix,
                isValid: isPhoneNumberValid(prefix + phoneNumber),
            });
        },
        prefixMask = phonePrefix.replace('+',
            '').length === 4 ? '+ 9-999' : '+ 9999',
        phoneMask = getMaskByPhonePrefix(phonePrefix.replace('+',
            '')),
    }) => {
        const setUserError = React.useCallback(() => actions.setUserError,
            []);
        const safeArea = useSafeAreaInsets();
        useFocusEffect(setUserError);
        useFocusEffect(
            useCallback(() => {
                const ver = VERSION?.split('.')
                    .slice(0,
                        3)
                    .join('.');
                storage.set('syncedVersion',
                    ver);
                storage.set(FIRST_AID_KIT_SYNC_KEY,
                    ver);
                storage.set('authVersion',
                    ver);
                //storage.set(MY_DOCUMENTS_SYNC_KEY, ver);
            },
            []),
        );
        useFocusEffect(
            useCallback(() => {
                api.getLoginMethods()
                    .then((resp) => {
                        putIntoStore('emailLoginEnabled', resp?.emailLoginEnabled ?? false);
                    })
                    .catch(() => {
                        putIntoStore('emailLoginEnabled', false);
                    });
            }, []),
        );
        // useEffect(setUserError);
        return (
            <Page
                name="sign-in"
                // persistKeyboard
                // onDidMount={onDidMount}
                notification={
                    error &&
                    (error instanceof Error
                        ? {
                            message: error.message,
                            code: error.code,
                            onSubmitPhone,
                        }
                        : {
                            ...error,
                            onSubmitPhone,
                        })
                }
                isLoading={isLoading || isFetching}
                safeArea={safeArea}
            >
                <KeyboardAvoidingView
                    behavior={Platform.OS !== 'ios' ? 'padding' : 'position'}
                    keyboardVerticalOffset={20}
                >
                    <Section>
                        {captchaCodeImg ? (
                            <Logo.Wrapper title="titles.enter_captcha_code">
                                <Image
                                    source={{ uri: captchaCodeImg }}
                                    style={{
                                        height: 60,
                                        width: 140,
                                        alignSelf: 'center',
                                        marginBottom: 8,
                                    }}
                                />
                                <TextInput.Number
                                    style={{ flex: 1 }}
                                    onChangeText={onCaptchaCode}
                                />
                            </Logo.Wrapper>
                        ) : (
                            <Logo.Wrapper
                                title="titles.enter_your_phone_number"
                                subtitle="titles.sms_activation_message"
                            >
                                <Field
                                    style={{
                                        flex: 1,
                                        flexDirection: 'row',
                                    }}
                                >
                                    <TextInput.MaskedNumber
                                        ns="phonePrefix"
                                        style={{ width: 86 }}
                                        onChangeText={onPhonePrefix}
                                        value={phonePrefix}
                                        mask={prefixMask}
                                        placeholder="+"
                                        disabled={MODE !== 'uat'}
                                    />
                                    {Platform.OS === 'ios' || Platform.OS === 'android' ? (
                                        <>
                                            <InputWrapper>
                                                <MaskedTextInput
                                                    style={[
                                                        st.input,
                                                        {
                                                            flex: 1,
                                                            marginLeft: 8,
                                                        },
                                                    ]}
                                                    mask={SIGNIN_MASK_MOBILE}
                                                    placeholder={SIGNIN_MASK.replace(/9/gi,
                                                        '_')}
                                                    autoSkip={true}
                                                    value={phoneNumber}
                                                    onChangeText={onPhoneNumber}
                                                    keyboardType="phone-pad"
                                                />
                                            </InputWrapper>
                                        </>
                                    ) : (
                                        <>
                                            {/* <MaskedInput style={{ flex: 1, marginLeft: 8 }} */}
                                            {/*    mask={phoneMask} */}
                                            {/*    placeholder={phoneMask.replace(/9/gi, '_')} */}
                                            {/*    onChangeText={onPhoneNumber} */}
                                            {/*    value={phoneNumber} */}
                                            {/* /> */}
                                            <TextInput.MaskedNumber
                                                style={{
                                                    flex: 1,
                                                    marginLeft: 8,
                                                }}
                                                onChangeText={onPhoneNumber}
                                                mask={SIGNIN_MASK}
                                                placeholder={SIGNIN_MASK.replace(/9/gi,
                                                    '_')}
                                                value={phoneNumber}
                                            />
                                        </>
                                    )}
                                    {/* <TextInput.MaskedNumber */}
                                    {/*     style={{ */}
                                    {/*         flex: 1, */}
                                    {/*         marginLeft: 8, */}
                                    {/*     }} */}
                                    {/*     onChangeText={onPhoneNumber} */}
                                    {/*     mask={phoneMask} */}
                                    {/*     placeholder={phoneMask.replace(/9/gi, '_')} */}
                                    {/*     value={phoneNumber} */}
                                    {/* /> */}
                                </Field>
                            </Logo.Wrapper>
                        )}
                        <BigButton
                            title="buttons.continue"
                            disabled={
                                captchaCodeImg
                                    ? !captchaCodeNum || !isValid || isCaptchaSubmitted
                                    : !isValid || waitingResponse
                            }
                            action={
                                waitingResponse
                                    ? () => {
                                    }
                                    : () => {
                                        Object.isGetCodeConfirmation = true;
                                        eventBus.on('RESTORE',
                                            checkRestore);

                                        setState({ waitingResponse: true });
                                        (captchaCodeImg ? onSubmitCaptchaCode : onSubmitPhone)();
                                    }
                            }
                        />
                        {emailLoginEnabled && !captchaCodeImg && (
                            <Link
                                underline
                                bigger
                                id="titles.loginWithEmail"
                                href={() => {
                                    Object.trackAction('email_login_start');
                                    navigation.navigate('EmailSignIn');
                                }}
                                style={{ marginTop: 16, alignSelf: 'center' }}
                            />
                        )}
                    </Section>
                </KeyboardAvoidingView>
            </Page>
        );
    },
    // props
    {
        isFetching: { from: getIsUserFetching },
        isLoading: { from: getUserLoadingState },
        error: { from: getUserError },
        defaultSmsProvider: { from: getDefaultSmsProvider },
        secondSmsProvider: { from: getSecondSmsProvider },
        emailLoginEnabled: { from: (state) => state.db?.emailLoginEnabled },
    },
);
